import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { IEmbeddedState } from './IEmbeddedState';
import { EmbeddedStateChangedEvent } from './events/EmbeddedStateChangedEvent';
import { FilterSelectionStateCommittedEvent } from '../filters/events/FilterSelectionStateCommittedEvent';
import { FilterSelectionStateCommitIgnoredEvent } from '../filters/events/FilterSelectionStateCommitIgnoredEvent';
import { DegreeCommittedEvent } from '@/store/events/DegreeCommittedEvent';
import { DegreeCommitIgnoredEvent } from '@/store/events/DegreeCommitIgnoredEvent';
import { EmbeddedStateCommittedEvent } from './events/EmbeddedStateCommittedEvent';
import { EmbeddedStateIgnoredEvent } from './events/EmbeddedStateIgnoredEvent';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { PaginationCommittedEvent } from '@/store/events/PaginationCommittedEvent';
import { PaginationCommitIgnoredEvent } from '@/store/events/PaginationCommitIgnoredEvent';
import { EmbeddedStatePropagatedEvent } from './events/EmbeddedStatePropagatedEvent';
import { SortingStateCommittedEvent } from '@/sorting/events/SortingStateCommittedEvent';
import { SortingStateCommitIgnoredEvent } from '@/sorting/events/SortingStateCommitIgnoredEvent';

export class EmbeddedStatePropagation implements
	ISubscriber<FilterSelectionStateCommittedEvent>,
	ISubscriber<FilterSelectionStateCommitIgnoredEvent>,
	ISubscriber<SortingStateCommittedEvent>,
	ISubscriber<SortingStateCommitIgnoredEvent>,
	ISubscriber<DegreeCommittedEvent>,
	ISubscriber<DegreeCommitIgnoredEvent>,
	ISubscriber<PaginationCommittedEvent>,
	ISubscriber<PaginationCommitIgnoredEvent>
{
	private static CorrelationId = 0;

	private readonly eventAggregationService: EventAggregationService;
	private readonly correlationId: number;

	private embeddedState: IEmbeddedState;
	private isFilterSelectionCommitted = false;
	private isFilterSelectionIgnored = false;
	private isSortingSelectionCommitted = false;
	private isSortingSelectionIgnored = false;
	private isDegreeCommitted = false;
	private isDegreeIgnored = false;
	private isPageCommitted = false;
	private isPageIgnored = false;

	private readonly isInitialNavigation: boolean;

	private promiseResolve?: () => void;
	public readonly promise: Promise<void>;

	public constructor(
		eventAggregationService: EventAggregationService,
		embeddedState: IEmbeddedState,
		isInitialNavigation: boolean
	) {
		this.correlationId = ++EmbeddedStatePropagation.CorrelationId;

		this.promise = new Promise((resolve) => (this.promiseResolve = resolve));

		this.embeddedState = embeddedState;
		this.isInitialNavigation = isInitialNavigation;

		this.eventAggregationService = eventAggregationService;
		this.eventAggregationService.subscribeTo(FilterSelectionStateCommittedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(FilterSelectionStateCommitIgnoredEvent.EventType, this);
		this.eventAggregationService.subscribeTo(SortingStateCommittedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(SortingStateCommitIgnoredEvent.EventType, this);
		this.eventAggregationService.subscribeTo(DegreeCommittedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(DegreeCommitIgnoredEvent.EventType, this);
		this.eventAggregationService.subscribeTo(PaginationCommittedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(PaginationCommitIgnoredEvent.EventType, this);
	}

	public get propagated(): boolean {
		return this.isFilterSelectionPropagated && this.isSortingSelectionPropagated && this.isDegreePropagated && this.isPagePropagated;
	}

	public get ignored(): boolean {
		return this.isFilterSelectionIgnored && this.isSortingSelectionIgnored && this.isDegreeIgnored && this.isPageIgnored;
	}

	private get isFilterSelectionPropagated(): boolean {
		return this.isFilterSelectionCommitted || this.isFilterSelectionIgnored;
	}

	private get isSortingSelectionPropagated(): boolean {
		return this.isSortingSelectionCommitted || this.isSortingSelectionIgnored;
	}

	private get isDegreePropagated(): boolean {
		return this.isDegreeCommitted || this.isDegreeIgnored;
	}

	private get isPagePropagated(): boolean {
		return this.isPageCommitted || this.isPageIgnored;
	}

	public notify(
		event:
		| FilterSelectionStateCommittedEvent
		| FilterSelectionStateCommitIgnoredEvent
		| SortingStateCommittedEvent
		| SortingStateCommitIgnoredEvent
		| DegreeCommittedEvent
		| DegreeCommitIgnoredEvent
		| PaginationCommittedEvent
		| PaginationCommitIgnoredEvent
	): void {
		if (event.correlationId !== this.correlationId || this.propagated) {
			return;
		}

		if (FilterSelectionStateCommittedEvent.is(event)) {
			this.isFilterSelectionCommitted = true;
		}

		if (FilterSelectionStateCommitIgnoredEvent.is(event)) {
			this.isFilterSelectionIgnored = true;
		}

		if (SortingStateCommittedEvent.is(event)) {
			this.isSortingSelectionCommitted = true;
		}

		if (SortingStateCommitIgnoredEvent.is(event)) {
			this.isSortingSelectionIgnored = true;
		}

		if (DegreeCommittedEvent.is(event)) {
			this.isDegreeCommitted = true;
		}

		if (DegreeCommitIgnoredEvent.is(event)) {
			this.isDegreeIgnored = true;
		}

		if (PaginationCommittedEvent.is(event)) {
			this.isPageCommitted = true;
		}

		if (PaginationCommitIgnoredEvent.is(event)) {
			this.isPageIgnored = true;
		}

		if (this.propagated && !this.ignored) {
			this.publishCommitted();
		}

		if (this.propagated && this.ignored) {
			this.publishIgnored();
		}

		if (this.propagated) {
			this.onPropagated();
		}
	}

	public begin(): void {
		this.eventAggregationService.publish(new EmbeddedStateChangedEvent(this.embeddedState, this.correlationId));
	}

	private publishCommitted(): void {
		this.eventAggregationService.publish(
			new EmbeddedStateCommittedEvent(this.correlationId, this.embeddedState, this.isInitialNavigation)
		);
	}

	private publishIgnored(): void {
		this.eventAggregationService.publish(new EmbeddedStateIgnoredEvent(this.correlationId, this.isInitialNavigation));
	}

	private onPropagated(): void {
		this.eventAggregationService.publishTo(
			EmbeddedStatePropagatedEvent.EventType,
			new EmbeddedStatePropagatedEvent(this.correlationId, this.isInitialNavigation, this.ignored, this.embeddedState)
		);

		if (this.promiseResolve) {
			this.promiseResolve();
		}
	}
}
